import { acSetDisconnect } from "./ActionAuth"

const JSON_HEADER = { 'Accept': 'application/json', 'Content-Type': 'application/json' }
const FORM_DATA_HEADER = { 'Accept': 'application/json' }

const buildJsonHeader = (m, b) => ({
    method: m,
    headers: JSON_HEADER,
    body: JSON.stringify(b),
    credentials: 'include'
})

const buildFormDataHeader = (m, b) => ({
    method: m,
    headers: FORM_DATA_HEADER,
    body: b,
    credentials: 'include'
})

export const RESPONSE_TYPE = {
    JSON: 'json',
    TEXT: 'text',
    NONE: 'none',
    RESPONSE: 'response',
}

const handleFetchErrors = res => {
    return new Promise((resolve, reject) => {
        if (res.ok) { return resolve(res) }
        res.json()
            .then(j => {
                const headers = {}
                res.headers.forEach((value, name) => {
                    headers[name] = value
                })
                const err = { ...j, headers }
                reject(err)
            })
            .catch(_ => reject(res))
    })
}

/**
 * @description Executes a fetch call and handles the response flow.
 *
 * @author Maria Eduarda Franke
 * @param {Function} fetchCall - The fetch call to execute.
 * @param {Function} dispatch - The dispatch function from Redux.
 * @param {Function} onSuccess - The success action creator.
 * @param {Function} onFailure - The failure action creator.
 * @param {string} [respType=RESPONSE_TYPE.JSON] - The response type.
 */
const fetchFlow = (fetchCall, dispatch, onSuccess, onFailure, respType = RESPONSE_TYPE.JSON) => {
    return new Promise((resolve, reject) => {
        fetchCall()
            .then(handleFetchErrors)
            .then(res => {
                if (res.status === 204 && respType !== RESPONSE_TYPE.NONE) {
                    console.warn('Trying to get data from a 204 Response!')
                    return undefined
                }

                switch (respType) {
                    case RESPONSE_TYPE.JSON: return res.json()
                    case RESPONSE_TYPE.TEXT: return res.text()
                    case RESPONSE_TYPE.NONE: return undefined
                    default: return res
                }
            })
            .then(result => {
                dispatch(onSuccess(result))
                resolve(result)
            })
            .catch(err => {
                // If the error status is 401 (and path equal authenticate), it will redirect the user to login, as the session is expired.
                if (err.status === 401) {

                    dispatch(acSetDisconnect())

                    if (err.path === 'authenticate') {
                        window.location.href = '/'
                    }
                }

                dispatch(onFailure(err))
                reject(err)
            })
    })
}

/**
 * @description Dispatches a fetch request and handles the flow of the request.
 *
 * @author Maria Eduarda Franke
 * @param {Function} fetchCall - The fetch call to be executed.
 * @param {Function} onBegin - The action to be dispatched when the fetch request begins.
 * @param {Function} onSuccess - The action to be dispatched when the fetch request is successful.
 * @param {Function} onFailure - The action to be dispatched when the fetch request fails.
 */
const dispatchFetch = (fetchCall, onBegin, onSuccess, onFailure, respType = RESPONSE_TYPE.JSON) => {
    return dispatch => {
        dispatch(onBegin())
        return fetchFlow(fetchCall, dispatch, onSuccess, onFailure, respType)
    }
}

/**
 * @description Dispatches a GET request to the specified URL and handles the response using the provided callbacks.
 *
 * @author Maria Eduarda Franke
 * @param {string} url - The URL to send the GET request to.
 * @param {function} onBegin - The callback function to be called before the request is sent.
 * @param {function} onSuccess - The callback function to be called if the request is successful.
 * @param {function} onFailure - The callback function to be called if the request fails.
 */
export const dispatchFetchGet = (url, onBegin, onSuccess, onFailure, respType = RESPONSE_TYPE.JSON) => {
    const fCall = () => fetch(url, { method: 'GET', credentials: 'include' })
    return dispatchFetch(fCall, onBegin, onSuccess, onFailure, respType)
}

/**
 * @description Dispatches a POST request to the specified URL with the given body.
 * 
 * @author Maria Eduarda Franke
 * @param {string} url - The URL to send the request to.
 * @param {object} body - The body of the request.
 * @param {function} onBegin - The action to dispatch when the request begins.
 * @param {function} onSuccess - The action to dispatch when the request is successful.
 * @param {function} onFailure - The action to dispatch when the request fails.
 */
export const dispatchFetchPost = (url, body, onBegin, onSuccess, onFailure, respType = RESPONSE_TYPE.JSON) => {
    const fCall = () => fetch(url, buildJsonHeader('POST', body || {}))
    return dispatchFetch(fCall, onBegin, onSuccess, onFailure, respType)
}

/**
 * @description Dispatches a POST request to the specified URL with the given form data body.
 * 
 * @author Maria Eduarda Franke
 * @param {string} url - The URL to send the request to.
 * @param {object} body - The form data body of the request.
 * @param {function} onBegin - The action to dispatch when the request begins.
 * @param {function} onSuccess - The action to dispatch when the request is successful.
 * @param {function} onFailure - The action to dispatch when the request fails.
 */
export const dispatchFetchPostFormData = (url, body, onBegin, onSuccess, onFailure, respType = RESPONSE_TYPE.JSON) => {
    const fCall = () => fetch(url, buildFormDataHeader('POST', body))
    return dispatchFetch(fCall, onBegin, onSuccess, onFailure, respType)
}

/**
 * @description Sends a PUT request to the specified URL with the provided body.
 * 
 * @author Maria Eduarda Franke
 * @param {string} url - The URL to send the request to.
 * @param {object} body - The body of the request.
 * @param {function} onBegin - The action to dispatch when the request begins.
 * @param {function} onSuccess - The action to dispatch when the request is successful.
 * @param {function} onFailure - The action to dispatch when the request fails.
 */
export const dispatchFetchPut = (url, body, onBegin, onSuccess, onFailure, respType = RESPONSE_TYPE.JSON) => {
    const fCall = () => fetch(url, buildJsonHeader('PUT', body || {}))
    return dispatchFetch(fCall, onBegin, onSuccess, onFailure, respType)
}

/**
 * @description Dispatches a DELETE request to the specified URL and handles the response.
 *
 * @author Maria Eduarda Franke
 * @param {string} url - The URL to send the DELETE request to.
 * @param {function} onBegin - The action to dispatch when the request begins.
 * @param {function} onSuccess - The action to dispatch when the request is successful.
 * @param {function} onFailure - The action to dispatch when the request fails.
 */
export const dispatchFetchDelete = (url, onBegin, onSuccess, onFailure, respType = RESPONSE_TYPE.JSON) => {
    const fCall = () => fetch(url, { method: 'DELETE', credentials: 'include' })
    return dispatchFetch(fCall, onBegin, onSuccess, onFailure, respType)
}

export const dispatchCustomFetch = (fetchCall, onBegin, onSuccess, onFailure, respType = RESPONSE_TYPE.JSON) => {
    return dispatchFetch(fetchCall, onBegin, onSuccess, onFailure, respType)
}